import React from "react";
import { Link } from "react-router-dom";
import { Phone, Link2 } from "lucide-react";
import { Card } from "../ui/card";
import { useAuth } from "../../lib/auth";

const refs = (j) => (j.personel_ref && j.personel_ref.length ? j.personel_ref : (j.personel || []).map((n) => ({ nama: n, jemaat_id: null })));
const waLink = (k) => { const n = (k.whatsapp || k.nomor_hp || "").replace(/\D/g, ""); return n ? `https://wa.me/${n.replace(/^0/, "62")}` : null; };

const Daftar = ({ j, kontak, bisaLihat, pemisah = ", " }) => { const r = refs(j); return r.length ? r.map((p, i) => <React.Fragment key={i}>{i > 0 && pemisah}<Nama p={p} kontak={kontak} bisaLihat={bisaLihat} /></React.Fragment>) : <span className="text-slate-400 italic">—</span>; };
const Nama = ({ p, kontak, bisaLihat }) => {
  const k = p.jemaat_id ? kontak?.[p.jemaat_id] : null;
  if (!p.jemaat_id) return <span>{p.nama}</span>;
  const wa = k && waLink(k);
  return (
    <span className="inline">
      {bisaLihat ? <Link to={`/jemaat/${p.jemaat_id}`} className="text-[hsl(224,64%,33%)] hover:underline inline-flex items-center gap-0.5" title="Buka profil jemaat" data-testid={`struktur-personel-link-${p.jemaat_id}`}><Link2 className="h-3 w-3" />{p.nama}</Link> : <span className="inline-flex items-center gap-0.5"><Link2 className="h-3 w-3 text-slate-400" />{p.nama}</span>}
      {wa && <a href={wa} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-800" title={`WhatsApp ${k.whatsapp || k.nomor_hp}`} aria-label="Hubungi via WhatsApp" data-testid={`struktur-personel-wa-${p.jemaat_id}`}><Phone className="h-3 w-3" /></a>}
    </span>
  );
};

const Kotak = ({ judul, j, warna = "amber", testId, kontak, bisaLihat }) => (
  <div className={`rounded-xl border overflow-hidden text-sm min-w-[180px] ${warna === "amber" ? "border-amber-300" : "border-emerald-300"}`} data-testid={testId}>
    <div className={`px-3 py-1.5 font-semibold text-center ${warna === "amber" ? "bg-amber-400 text-amber-950" : "bg-emerald-500 text-white"}`}>{judul}</div>
    <div className="px-3 py-1.5 bg-white text-center text-slate-800">{j && refs(j).length ? refs(j).map((p, i) => <div key={i}><Nama p={p} kontak={kontak} bisaLihat={bisaLihat} /></div>) : <span className="text-slate-400 italic">—</span>}</div>
  </div>
);

const KOORD = "koordinator";
const SubUnit = ({ u, kontak, bisaLihat }) => {
  const jab = u.jabatan || [];
  const tunggal = jab.length === 1 && jab[0].nama.trim().toLowerCase() === KOORD;
  return (
    <div className="rounded-lg border border-emerald-200 overflow-hidden text-xs" data-testid={`struktur-subunit-${u.id}`}>
      {tunggal
        ? <div className="flex flex-wrap items-baseline gap-x-1 px-2 py-1.5"><span className="font-semibold text-emerald-900">{u.nama}</span><span className="text-slate-500">:</span><span className="text-slate-900 break-words min-w-0" data-testid={`struktur-subunit-personel-${u.id}`}><Daftar j={jab[0]} kontak={kontak} bisaLihat={bisaLihat} /></span></div>
        : <>
          <div className="bg-emerald-100 text-emerald-900 font-semibold px-2 py-1 break-words">{u.nama}</div>
          <div className="bg-white px-2 py-1.5 space-y-1">
            {jab.map((j) => <div key={j.id} className="flex gap-1 items-baseline"><span className="text-slate-600 shrink-0 whitespace-nowrap">{j.nama}</span><span className="text-slate-500 shrink-0">:</span><span className="text-slate-900 min-w-0 flex-1 break-normal leading-snug"><Daftar j={j} kontak={kontak} bisaLihat={bisaLihat} /></span></div>)}
            {u.keterangan?.map((k, i) => <div key={i} className="text-slate-800 break-words">- {k}</div>)}
            {!jab.length && !u.keterangan?.length && <span className="text-slate-400 italic">Belum ada jabatan</span>}
          </div>
        </>}
    </div>
  );
};

const UnitCard = ({ u, kontak, bisaLihat }) => {
  const kepala = u.jabatan?.[0];
  return (
    <div className="space-y-2" data-testid={`struktur-unit-${u.id}`}>
      <Kotak judul={u.nama} j={kepala} kontak={kontak} bisaLihat={bisaLihat} testId={`struktur-unit-kotak-${u.id}`} />
      {u.jabatan?.length > 1 && <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs space-y-0.5">{u.jabatan.slice(1).map((j) => <div key={j.id}><span className="text-slate-600">{j.nama}:</span> <Daftar j={j} kontak={kontak} bisaLihat={bisaLihat} /></div>)}</div>}
      {u.keterangan?.length > 0 && <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs">{u.keterangan.map((k, i) => <div key={i}>- {k}</div>)}</div>}
      {u.sub_unit?.length > 0 && <div className="pl-3 border-l-2 border-slate-300 space-y-2">{u.sub_unit.map((s) => <SubUnit key={s.id} u={s} kontak={kontak} bisaLihat={bisaLihat} />)}</div>}
    </div>
  );
};

export const BaganStruktur = ({ data }) => {
  const { can } = useAuth(); const bisaLihat = can("jemaat:read"); const kontak = data.kontak || {};
  const pim = (nama) => data.pimpinan?.find((j) => j.nama.toLowerCase() === nama.toLowerCase());
  const utama = ["Gembala Sidang", "Sekretaris", "Bendahara", "Wakil Gembala Bidang Ibadah", "Wakil Gembala Bidang Diakonia"];
  const lainnya = (data.pimpinan || []).filter((j) => !utama.some((u) => u.toLowerCase() === j.nama.toLowerCase()));
  return (
    <Card className="p-4 sm:p-6 rounded-2xl overflow-x-auto" data-testid="struktur-bagan">
      <div className="text-center mb-5"><div className="text-xs uppercase tracking-widest text-slate-500">Susunan Kepengurusan GKPY Jatinegara</div><h2 className="text-base md:text-lg font-bold text-slate-900">{data.nama}</h2></div>
      <div className="flex flex-col items-center gap-3">
        <Kotak judul="Gembala Sidang" j={pim("Gembala Sidang")} kontak={kontak} bisaLihat={bisaLihat} testId="struktur-gembala" />
        <div className="h-4 w-px bg-slate-300" />
        <div className="flex flex-wrap justify-center gap-3">
          <Kotak judul="Sekretaris" j={pim("Sekretaris")} kontak={kontak} bisaLihat={bisaLihat} testId="struktur-sekretaris" />
          <Kotak judul="Bendahara" j={pim("Bendahara")} kontak={kontak} bisaLihat={bisaLihat} testId="struktur-bendahara" />
          {lainnya.map((j) => <Kotak key={j.id} judul={j.nama} j={j} kontak={kontak} bisaLihat={bisaLihat} testId={`struktur-pimpinan-${j.id}`} />)}
        </div>
        <div className="h-4 w-px bg-slate-300" />
      </div>
      <div className="grid gap-6 mt-2 grid-cols-1">
        {(data.bidang || []).map((b) => {
          const wakil = data.pimpinan?.find((j) => j.nama.toLowerCase().includes(b.nama.toLowerCase().replace("bidang ", "")) && j.nama.toLowerCase().startsWith("wakil"));
          return (
            <div key={b.id} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3 sm:p-4" data-testid={`struktur-bidang-${b.id}`}>
              <div className="flex flex-col items-center mb-3"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{b.nama}</div>{wakil && <Kotak judul={wakil.nama} j={wakil} kontak={kontak} bisaLihat={bisaLihat} testId={`struktur-wakil-${b.id}`} />}</div>
              <div className={`grid gap-3 ${b.unit?.length >= 3 ? "sm:grid-cols-2 lg:grid-cols-3" : b.unit?.length === 2 ? "sm:grid-cols-2" : ""}`}>{(b.unit || []).map((u) => <UnitCard key={u.id} u={u} kontak={kontak} bisaLihat={bisaLihat} />)}</div>
              {!b.unit?.length && <div className="text-xs text-slate-400 italic text-center">Belum ada unit</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
